import { useMemo, useState } from "react";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import Select from "react-select";
import SideModal from "/src/components/modal/SideModal/SideModal";
import useModalHelper from "/src/components/modal/hooks/useModalHelper";
import Form from "/src/components/form/Form";
import Field from "/src/components/UI/Field/Field";
import TextInput from "/src/components/UI/TextField/TextInput";
import TextArea from "/src/components/UI/TextField/TextArea";
import CheckBox from "/src/components/UI/CheckBox/CheckBox";
import Button from "/src/components/UI/Button/Button";
import { useForm } from "react-hook-form";
import roleAdminService from "../../services/roleAdminService";
import useUnitLabels from "/src/hooks/useUnitLabels";
import "./RoleModal.css";

// Only "view_conversations" can be restricted to a set of units for now.
const UNIT_SCOPED_PERMISSION_KEY = "view_conversations";

const UNIT_GROUP_COLORS = [
    "#4c9aff", "#36b37e", "#ff8b00", "#c33b8f",
    "#00b8d9", "#6554c0", "#ff5630", "#8993a4"
];

const RoleModal = NiceModal.create(({ title, data, permissions = [] }) => {
    const modal = useModal();
    const { onClose } = useModalHelper(modal);
    const { createRole, updateRole } = roleAdminService;
    const { unitLabels } = useUnitLabels();

    const { register, handleSubmit, formState: { errors, isValid } } = useForm({
        mode: "onChange",
        defaultValues: { name: data?.name || "", description: data?.description || "" },
    });

    const [selectedIds, setSelectedIds] = useState(new Set((data?.permissions || []).map((g) => g.permission_id)));
    const [unitIdsByPermission, setUnitIdsByPermission] = useState(
        Object.fromEntries((data?.permissions || []).map((g) => [g.permission_id, g.unit_ids || []]))
    );
    const [unitSearchInput, setUnitSearchInput] = useState("");

    const togglePermission = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const setUnitIdsForPermission = (permissionId, unitIds) => {
        setUnitIdsByPermission((prev) => ({ ...prev, [permissionId]: unitIds }));
    };

    const permissionsByCategory = permissions.reduce((acc, permission) => {
        acc[permission.category] = acc[permission.category] || [];
        acc[permission.category].push(permission);
        return acc;
    }, {});

    const unitScopedPermission = permissions.find((p) => p.key === UNIT_SCOPED_PERMISSION_KEY);
    const isUnitScopeEnabled = unitScopedPermission && selectedIds.has(unitScopedPermission.id);
    const selectedUnitIds = (unitScopedPermission && unitIdsByPermission[unitScopedPermission.id]) || [];

    // Group units by their first label word (e.g. "BG Non-Sale DE" -> "BG",
    // "Inbound DK" -> "Inbound") - same visual pattern as the criteria filter
    // dropdown (colored group dot + colored option bar), no flat wall of 98 options.
    const unitGroupColorByName = useMemo(() => {
        const names = [...new Set(unitLabels.map((u) => u.label.split(" ")[0]))];
        return Object.fromEntries(names.map((name, i) => [name, UNIT_GROUP_COLORS[i % UNIT_GROUP_COLORS.length]]));
    }, [unitLabels]);

    const groupedUnitOptions = useMemo(() => {
        const groups = {};
        unitLabels.forEach((u) => {
            const groupName = u.label.split(" ")[0];
            groups[groupName] = groups[groupName] || [];
            groups[groupName].push({ value: u.unit_id, label: u.label, group: groupName });
        });
        return Object.entries(groups).map(([groupName, options]) => ({
            label: groupName,
            color: unitGroupColorByName[groupName],
            options,
        }));
    }, [unitLabels, unitGroupColorByName]);

    const unitOptionsFlat = useMemo(() => groupedUnitOptions.flatMap((g) => g.options), [groupedUnitOptions]);

    const onSubmit = async (values) => {
        const grantedPermissions = Array.from(selectedIds).map((permission_id) => {
            const unitIds = unitIdsByPermission[permission_id];
            return { permission_id, unit_ids: unitIds && unitIds.length > 0 ? unitIds : null };
        });

        const payload = { ...values, permissions: grantedPermissions };

        const [response, error] = data
            ? await updateRole(data.id, payload)
            : await createRole(payload);

        if (error) {
            console.error(error);
            return;
        }
        modal.resolve(response);
        onClose();
    };

    return (
        <SideModal name="role-modal" id={modal.id} autoClose="true" onClose={onClose} style={{ width: "550px" }}>
            <div className="button-leave" id="close_modal_container" onClick={() => onClose()}>
                <i id="close_modal" className="fa fa-times" aria-hidden="true"></i>
            </div>
            <Form className="role-form" onSubmit={handleSubmit(onSubmit)}>
                <Form.Title>{title}</Form.Title>
                <Form.Body className="role-form-body">
                    <Field label={<span>Name <span className="obligatory_field">*</span></span>}>
                        <TextInput {...register("name", { required: true })} />
                        <span className="error-display">{errors.name ? "Name is required" : ""}</span>
                    </Field>

                    <Field label="Description">
                        <TextArea {...register("description")} style={{ height: "60px" }} />
                    </Field>

                    <Field label="Permissions">
                        <div className="role-form-permissions">
                            {Object.entries(permissionsByCategory).map(([category, items]) => (
                                <div className="role-form-permission-category" key={category}>
                                    <div className="role-form-permission-category-title">{category}</div>
                                    {items.map((permission) => (
                                        <label className="role-form-permission-row" key={permission.id}>
                                            <CheckBox
                                                name={"permission-" + permission.id}
                                                checked={selectedIds.has(permission.id)}
                                                onChange={() => togglePermission(permission.id)}
                                            />
                                            <span>{permission.label}</span>
                                        </label>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </Field>

                    {unitScopedPermission && (
                        <Field label={`Units (for "${unitScopedPermission.label}")`}>
                            <div className="role-form-unit-scope">
                                {!isUnitScopeEnabled ? (
                                    <p className="role-form-unit-scope_hint">
                                        Enable "{unitScopedPermission.label}" above to restrict it to specific units.
                                    </p>
                                ) : (
                                    <Select
                                        classNamePrefix="role-unit-select"
                                        isMulti
                                        options={groupedUnitOptions}
                                        value={unitOptionsFlat.filter((o) => selectedUnitIds.includes(o.value))}
                                        getOptionValue={(o) => o.value}
                                        onChange={(selected) =>
                                            setUnitIdsForPermission(unitScopedPermission.id, selected ? selected.map((o) => o.value) : [])
                                        }
                                        hideSelectedOptions={false}
                                        inputValue={unitSearchInput}
                                        onInputChange={(value, actionMeta) => {
                                            // react-select clears its search text on select/blur by
                                            // default (action !== "input-change"); ignoring those keeps
                                            // what the user typed so clicking an option doesn't wipe it.
                                            if (actionMeta.action === "input-change") setUnitSearchInput(value);
                                        }}
                                        formatGroupLabel={(group) => (
                                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: group.color }} />
                                                <span>{group.label}</span>
                                            </div>
                                        )}
                                        formatOptionLabel={(opt) => (
                                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                <span style={{ width: "4px", alignSelf: "stretch", borderRadius: "2px", background: unitGroupColorByName[opt.group] }} />
                                                <span>{opt.label}</span>
                                            </div>
                                        )}
                                        styles={{
                                            option: (base, state) => ({
                                                ...base,
                                                backgroundColor: state.isSelected ? "var(--secondary)" : base.backgroundColor,
                                                color: state.isSelected ? "var(--main)" : base.color,
                                            }),
                                            groupHeading: (base, state) => ({
                                                ...base,
                                                color: state.data.color,
                                                fontWeight: 600,
                                                fontSize: "0.85em",
                                                textTransform: "none",
                                            }),
                                            container: (base) => ({ ...base, width: "100%" }),
                                            // Extra breathing room below the open menu so it doesn't sit
                                            // flush against the side modal's bottom edge.
                                            menu: (base) => ({ ...base, zIndex: 5, marginBottom: 16 }),
                                        }}
                                        controlShouldRenderValue={false}
                                        closeMenuOnSelect={false}
                                        placeholder={
                                            selectedUnitIds.length
                                                ? `${selectedUnitIds.length} unit${selectedUnitIds.length > 1 ? "s" : ""} selected`
                                                : "All units (no restriction)..."
                                        }
                                    />
                                )}

                                {isUnitScopeEnabled && selectedUnitIds.length > 0 && (
                                    <div className="role-form-unit-scope_selected">
                                        {selectedUnitIds.map((unitId) => {
                                            const option = unitOptionsFlat.find((o) => o.value === unitId);
                                            if (!option) return null;
                                            return (
                                                <span
                                                    key={unitId}
                                                    className="role-form-unit-chip"
                                                    style={{ background: unitGroupColorByName[option.group] }}
                                                    title="Click to remove"
                                                    onClick={() =>
                                                        setUnitIdsForPermission(
                                                            unitScopedPermission.id,
                                                            selectedUnitIds.filter((id) => id !== unitId)
                                                        )
                                                    }
                                                >
                                                    {option.label}
                                                    <i className="fas fa-times role-form-unit-chip_remove"></i>
                                                </span>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </Field>
                    )}
                </Form.Body>
                <Form.Buttons className="role-form-buttons">
                    <Button type="submit" label="Save" className="validate" disabled={!isValid} />
                </Form.Buttons>
            </Form>
        </SideModal>
    );
});

export default RoleModal;
